import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import { CheckboxController } from '@components-clean/core/core-checkbox';
import { DatePickerController } from '@components-clean/core/date-picker';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { useUnsavedChangesConfirmation, useUnsavedChangesTracking } from '@hooks-clean/index';
import { useAssociatedReviewLogs } from '@hooks-clean/use-associated-review-logs';
import { ObservationType, Owner } from '@interfaces-clean/observations.interface';
import { ObservationDetails } from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/observation-details';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { getUTCDate } from '@utils-clean/date-time-helpers';
import { NamedItemType } from 'types/graphql-global-types';
import { useLocalState, useTranslation } from '@hooks/index';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import {
    getNamedItemsByType,
    getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem,
    getNamedItemsByTypeVariables,
} from '@queries/generated/common.generated';
import {
    getStudyMembers,
    getStudyMembers_OpraQueries_studyMembers_StudyMember,
    getStudyMembersVariables,
} from '@queries/generated/studies.generated';
import { GET_STUDY_MEMBERS } from '@queries/studies';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface AddActionForm {
    observationId: string | undefined;
    type: ObservationType;
    requestedDate: Date | null;
    dueDate: Date | null;
    owner: Owner | null;
    responsibleFunction: { ref: string | null; name: string } | null;
    description: string | null;
    comments: string | null;
    resolution: string | null;
    completedDate: Date | null;
    associatedReviewLog: { id: number; datasetDate: string } | null;
    integrationEnabled: boolean;
}

interface Props {
    observationId: string | undefined;
    studyRef: string | null;
    onSubmit: (data: AddActionForm) => void;
    onClose: () => void;
    type: ObservationType;
}

const COMPONENT_NAME = 'add-action-dialog';

const AddActionDialog = ({ observationId, studyRef, onSubmit, onClose, type }: Props) => {
    const { t } = useTranslation('cm');
    const { state } = useLocalState();
    const study = state?.selectedStudy;

    const { data: observation, isLoading: loadingObservation } = ObservationsApi.useGetDetails(
        studyRef || '',
        observationId || '',
        type,
    );

    const [commentsExpanded, setCommentsExpanded] = useState<boolean>(false);
    const [resolutionExpanded, setResolutionExpanded] = useState<boolean>(false);
    const { data: associatedReviewLogs, isLoading: loadingReviewLogs } = useAssociatedReviewLogs(
        type,
        studyRef,
        observationId,
    );

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<AddActionForm>({
        mode: 'all',
        defaultValues: {
            observationId,
            type,
            requestedDate: getUTCDate(),
            dueDate: null,
            owner: null,
            responsibleFunction: null,
            description: '',
            comments: null,
            resolution: null,
            completedDate: null,
            associatedReviewLog: null,
            integrationEnabled: false,
        },
    });

    const { data: studyMembersData, loading: loadingStudyMembers } = useQuery<
        getStudyMembers,
        getStudyMembersVariables
    >(GET_STUDY_MEMBERS, {
        variables: { studyId: studyRef },
    });

    const [studyMembers, setStudyMembers] = useState<
        ({ fullName: string; ref: string; groupedBy: string } | null)[] | undefined
    >([]);

    useEffect(() => {
        if (studyMembersData?.studyMembers) {
            const studyUsers = studyMembersData.studyMembers
                ?.filter((user): user is getStudyMembers_OpraQueries_studyMembers_StudyMember => !!user)
                .map((user) => ({
                    fullName: `${user.firstName} ${user.lastName}`,
                    ref: user.id as string,
                    groupedBy: user.active ? 'Active' : 'Inactive',
                    disabled: !user.active,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.fullName.localeCompare(b.fullName);
                });
            setStudyMembers(studyUsers);
        }
    }, [studyMembersData?.studyMembers]);

    const { data: responsibleFunctionsData, loading: loadingResponsibleFunctions } = useQuery<
        getNamedItemsByType,
        getNamedItemsByTypeVariables
    >(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.RESPONSIBLE_FUNCTION,
        },
    });

    const [responsibleFunctions, setResponsibleFunctions] = useState<
        ({ groupedBy: string; ref: string; name: string; disabled: boolean } | null)[]
    >([]);

    useEffect(() => {
        if (responsibleFunctionsData?.namedItemsByType) {
            const studyFunctions = responsibleFunctionsData?.namedItemsByType
                .filter(
                    (ni): ni is getNamedItemsByType_OpraQueries_namedItemsByType_NamedItem => ni?.studyId === studyRef,
                )
                .flatMap((studyFunction) => ({
                    groupedBy: studyFunction.active ? 'Active' : 'Inactive',
                    name: studyFunction.name ?? '',
                    ref: (studyFunction.id as string) ?? '',
                    disabled: !studyFunction.active,
                }))
                .sort((a, b) => {
                    if (a.disabled !== b.disabled) {
                        return a.disabled ? 1 : -1;
                    }
                    return a.name.localeCompare(b.name);
                });
            setResponsibleFunctions(studyFunctions);
        }
    }, [responsibleFunctionsData?.namedItemsByType]);

    let dialogTitle = t('common:add');
    switch (type) {
        case ObservationType.Site:
            dialogTitle += ` ${t('actionDialog.siteTitle')}`;
            break;
        case ObservationType.Study:
            dialogTitle += ` ${t('actionDialog.studyTitle')}`;
            break;
        case ObservationType.Controls:
            dialogTitle += ` ${t('actionDialog.controlsTitle')}`;
            break;
        default:
            dialogTitle = '';
    }

    const primaryBtnLabel = t('common:add');

    useUnsavedChangesTracking({
        area: 'central-monitoring-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'central-monitoring-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        onClose();
    };

    const getDialogHeight = () => {
        switch (type) {
            case ObservationType.Study:
                return 616.5;
            default:
                return 650;
        }
    };

    const dialogWidth = 612;

    return (
        <DraggableDialog
            className={style.kWindowContent}
            dataTestId="add-observation-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmit)()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
            initialHeight={getDialogHeight()}
            initialWidth={dialogWidth}
            additionalInfo={
                <ObservationDetails
                    observation={observation || undefined}
                    observationLoading={loadingObservation}
                    type={type}
                />
            }
            additionalInfoWidth={385}
            additionalInfoBtnLabel={t('observation')}
            bodyPadding="none"
            setHeight="fit-content"
        >
            <div className={cn(style.editSiteObservationDialog, style.rightBorderStyle, 'k-w-full', 'k-h-full')}>
                <div>
                    {type === ObservationType.Site && (
                        <div className={cn(style.editActionInfo, 'k-mb-sm')}>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left k-flex-auto k-gap-sm">
                                <div className="k-font-medium">{t('actionDialog.site')}</div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>{observation?.siteName}</div>
                            </div>
                        </div>
                    )}
                    {type === ObservationType.Controls && (
                        <div className={cn(style.editActionInfo, 'k-mb-sm')}>
                            <div className="k-shrink-0 k-d-flex k-justify-content-left k-flex-auto k-gap-sm">
                                <div className="k-font-medium">{t('actionDialog.timePeriod')}</div>
                                <div className={cn(style.editSiteObservationTopInfoLabel)}>
                                    {observation?.timePeriod}
                                </div>
                            </div>
                        </div>
                    )}
                    <form
                        id="add-action"
                        className={cn({ 'k-pt-1.5': type !== ObservationType.Site }, 'k-w-full k-mt-0 k-pl-3 k-pr-3')}
                    >
                        <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                            <div className="!k-basis-1/2">
                                <BasicDropDownController
                                    label={t('actionDialog.associatedReviewLog')}
                                    control={control}
                                    name="associatedReviewLog"
                                    data-testid="add-action-associated-review-log"
                                    data={associatedReviewLogs ?? []}
                                    textField="datasetDate"
                                    dataItemKey="id"
                                    filterable
                                    loading={loadingReviewLogs}
                                    allowClear
                                    readOnly={isSubmitting}
                                />
                            </div>
                            <div className="!k-basis-1/2 k-pt-5">
                                {type === ObservationType.Site && study?.integrationActive && (
                                    <CheckboxController
                                        data-testid="integration-enabled"
                                        label={t('actionDialog.integrationEnabled')}
                                        name="integrationEnabled"
                                        control={control}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={cn('k-shrink-0 k-d-flex k-justify-content-center k-pb-md', style.formFieldGap)}>
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="requestedDate"
                                    data-testid="add-action-requested-date"
                                    fillMode="solid"
                                    required
                                    label={t('actionDialog.requestedDate')}
                                    readonly={isSubmitting}
                                />
                            </div>
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="dueDate"
                                    data-testid="add-action-due-date"
                                    fillMode="solid"
                                    label={t('actionDialog.dueDate')}
                                    readonly={isSubmitting}
                                />
                            </div>
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                            <BasicDropDownController
                                label={t('actionDialog.owner')}
                                control={control}
                                name="owner"
                                data-testid="add-action-owner"
                                data={studyMembers ?? []}
                                textField="fullName"
                                groupField="groupedBy"
                                dataItemKey="ref"
                                filterable
                                loading={loadingStudyMembers}
                                allowClear
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-center k-pb-md">
                            <BasicDropDownController
                                label={t('actionDialog.responsibleFunction')}
                                control={control}
                                name="responsibleFunction"
                                data-testid="add-action-responsible-function"
                                data={responsibleFunctions ?? []}
                                textField="name"
                                groupField="groupedBy"
                                dataItemKey="ref"
                                filterable
                                loading={loadingResponsibleFunctions}
                                allowClear
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaController
                                className={style.textAreaOverrides}
                                maxLength={3000}
                                control={control}
                                name="description"
                                required
                                data-testid="add-action-description"
                                label={t('actionDialog.description')}
                                rows={4}
                                readOnly={isSubmitting}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                control={control}
                                className={style.textAreaOverrides}
                                name="comments"
                                data-testid="field-select-comments"
                                label={t('actionDialog.comments')}
                                rows={4}
                                readOnly={isSubmitting}
                                handleInputChange={async (event) => {
                                    event.value.length > 0 ? setCommentsExpanded(true) : setCommentsExpanded(false);
                                }}
                                expanded={commentsExpanded}
                            />
                        </div>
                        <div className="k-d-flex k-flex-auto k-justify-content-center k-pb-md">
                            <TextAreaDropdownController
                                maxLength={3000}
                                control={control}
                                className={style.textAreaOverrides}
                                name="resolution"
                                data-testid="add-action-resolution"
                                label={t('actionDialog.resolution')}
                                rows={4}
                                readOnly={isSubmitting}
                                handleInputChange={async (event) => {
                                    event.value.length > 0 ? setResolutionExpanded(true) : setResolutionExpanded(false);
                                }}
                                expanded={resolutionExpanded}
                            />
                        </div>
                        <div className="k-shrink-0 k-d-flex k-justify-content-left k-pb-md">
                            <div className="!k-basis-1/2">
                                <DatePickerController
                                    control={control}
                                    name="completedDate"
                                    data-testid="add-action-completed-date"
                                    fillMode="solid"
                                    label={t('actionDialog.completedDate')}
                                    className="k-w-full"
                                    readonly={isSubmitting}
                                />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </DraggableDialog>
    );
};

export default AddActionDialog;
